import type {
  AdminAppointmentMutationResult,
  AppointmentMutationResult,
  AppointmentOperationStatus,
  AppointmentScope,
  BrowserAppointmentView,
  BrowserSchedulingContext,
} from './types';

const failUnless = (condition: boolean, message: string): void => {
  if (!condition) throw new Error(message);
};

const RFC3339_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(?:Z|[+-](\d{2}):(\d{2}))$/u;

const isLeapYear = (year: number): boolean =>
  year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);

export const isValidRfc3339 = (value: string): boolean => {
  const match = RFC3339_PATTERN.exec(value);
  if (match === null) return false;

  const [, yearText, monthText, dayText, hourText, minuteText, secondText] =
    match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const daysInMonth = [
    31,
    isLeapYear(year) ? 29 : 28,
    31,
    30,
    31,
    30,
    31,
    31,
    30,
    31,
    30,
    31,
  ];
  return (
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= daysInMonth[month - 1]! &&
    Number(hourText) <= 23 &&
    Number(minuteText) <= 59 &&
    Number(secondText) <= 59 &&
    (match[7] === undefined ||
      (Number(match[7]) <= 23 && Number(match[8]) <= 59))
  );
};

export const parseAppointmentTimestamp = (value: string): number =>
  Date.parse(value);

export const validateWireSemantics = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach(validateWireSemantics);
    return;
  }
  if (value === null || typeof value !== 'object') return;

  const object = value as Record<string, unknown>;
  if (
    typeof object.start_at === 'string' &&
    typeof object.end_at === 'string' &&
    parseAppointmentTimestamp(object.end_at) <=
      parseAppointmentTimestamp(object.start_at)
  ) {
    throw new Error('end_at must be after start_at');
  }
  Object.entries(object).forEach(([key, item]) => {
    if (
      (key === 'timezone' || key === 'viewer_timezone') &&
      typeof item === 'string'
    ) {
      try {
        new Intl.DateTimeFormat('en-US', { timeZone: item }).format();
      } catch {
        throw new Error(`${key} must be an IANA timezone`);
      }
    }
    if (key.endsWith('_at') && item !== null && typeof item === 'string') {
      if (!isValidRfc3339(item)) {
        throw new Error(`${key} must be a valid RFC3339 timestamp`);
      }
    }
    if (key === 'date' && typeof item === 'string') {
      const parsed = new Date(`${item}T00:00:00Z`);
      if (
        Number.isNaN(parsed.valueOf()) ||
        parsed.toISOString().slice(0, 10) !== item
      ) {
        throw new Error('date must be a valid YYYY-MM-DD value');
      }
    }
    validateWireSemantics(item);
  });
};

export const validateMutationResult = (
  result:
    | AppointmentMutationResult<BrowserAppointmentView>
    | AdminAppointmentMutationResult,
): void => {
  failUnless(
    result.status === 'rejected'
      ? result.appointments.length === 0 && result.rejection !== null
      : result.appointments.length > 0 && result.rejection === null,
    result.status === 'rejected'
      ? 'rejected result requires no Appointments and a rejection'
      : 'confirmed and pending results require Appointments and no rejection',
  );
};

export const validateBrowserAppointmentPage = (page: {
  appointments: Array<Pick<BrowserAppointmentView, 'currentSchedule' | 'id'>>;
}): void => {
  page.appointments.forEach((appointment, index, appointments) => {
    if (index === 0) return;
    const previous = appointments[index - 1]!;
    const previousStart = parseAppointmentTimestamp(
      previous.currentSchedule.startAt,
    );
    const currentStart = parseAppointmentTimestamp(
      appointment.currentSchedule.startAt,
    );
    failUnless(
      currentStart > previousStart ||
        (currentStart === previousStart && appointment.id > previous.id),
      'browser Appointments must be ordered by current start then id',
    );
  });
};

const validateEligibility = (
  eligibility: BrowserSchedulingContext['eligibility'],
): void => {
  failUnless(
    eligibility.eligible
      ? eligibility.reasons.length === 0
      : eligibility.reasons.length > 0,
    eligibility.eligible
      ? 'eligible context requires no reasons'
      : 'ineligible context requires a reason',
  );
};

const validateScopes = (scopes: AppointmentScope[]): void => {
  const canonical: AppointmentScope[] = ['appointment', 'this_and_following'];
  let previousPosition = -1;
  scopes.forEach((scope) => {
    const position = canonical.indexOf(scope);
    failUnless(
      position > previousPosition,
      'scopes must be unique and canonically ordered',
    );
    previousPosition = position;
  });
};

const validateLegacyAvailability = (
  context: BrowserSchedulingContext,
): void => {
  context.availability.days.forEach((day, index, days) => {
    failUnless(
      index === 0 || day.date > days[index - 1]!.date,
      'availability days must be uniquely ordered by date',
    );
    failUnless(
      day.enabled || day.slots.length === 0,
      'disabled availability day must have no slots',
    );
    failUnless(
      (context.kind !== 'chemistry' && context.intent !== 'reschedule') ||
        day.appointmentCount === null,
      'chemistry and reschedule day counts must be null',
    );
    day.slots.forEach((slot, slotIndex, slots) => {
      failUnless(
        slotIndex === 0 ||
          parseAppointmentTimestamp(slot.startAt) >
            parseAppointmentTimestamp(slots[slotIndex - 1]!.startAt),
        'availability slots must be uniquely ordered by start',
      );
    });
    failUnless(
      context.appointmentCount === null ||
        !day.enabled ||
        day.appointmentCount === context.appointmentCount,
      'top-level appointment count must match every enabled day',
    );
  });
};

const validateLegacySchedulingKindAndIntent = (
  context: BrowserSchedulingContext,
): void => {
  failUnless(
    context.kind !== 'chemistry' ||
      (context.seatType === null &&
        context.appointmentCount === null &&
        context.cadenceWeeks === null &&
        context.activeSeries === null),
    'chemistry context requires null seat, recurrence, and active Series',
  );
  // MEN-5306: same standalone-reschedule exemption as contractCodec.
  const standaloneReschedule =
    context.intent === 'reschedule' &&
    context.rescheduleTarget !== null &&
    context.rescheduleTarget.seriesId === null;
  failUnless(
    context.kind !== 'coaching' ||
      (context.seatType !== null &&
        (context.cadenceWeeks !== null || standaloneReschedule)),
    'coaching context requires seat type and cadence',
  );
  failUnless(
    context.intent !== 'book' || context.rescheduleTarget === null,
    'booking context requires null reschedule target',
  );
  failUnless(
    context.intent !== 'reschedule' || context.rescheduleTarget !== null,
    'rescheduling context requires a target',
  );
  failUnless(
    context.intent !== 'reschedule' || context.appointmentCount === null,
    'reschedule context requires null appointment count',
  );
};

const validateLegacyRescheduleTarget = (
  context: BrowserSchedulingContext,
): void => {
  const target = context.rescheduleTarget;
  if (target === null) return;

  validateScopes(target.allowedScopes);
  failUnless(
    context.kind !== 'chemistry' ||
      (target.seriesId === null &&
        (target.allowedScopes.length === 0 ||
          (target.allowedScopes.length === 1 &&
            target.allowedScopes[0] === 'appointment'))),
    'chemistry target must be standalone and appointment-scoped',
  );
  failUnless(
    context.kind !== 'chemistry' ||
      !context.eligibility.eligible ||
      (target.allowedScopes.length === 1 &&
        target.allowedScopes[0] === 'appointment'),
    'eligible chemistry target must allow appointment scope',
  );
  failUnless(
    context.kind !== 'coaching' ||
      context.intent !== 'reschedule' ||
      context.activeSeries === null ||
      target.seriesId === context.activeSeries.id,
    'active Series must identify the reschedule target Series',
  );
};

export const validateLegacyBrowserSchedulingContext = (
  context: BrowserSchedulingContext,
): void => {
  validateEligibility(context.eligibility);
  validateLegacySchedulingKindAndIntent(context);
  validateLegacyRescheduleTarget(context);
  failUnless(
    context.activeSeries === null ||
      context.kind !== 'coaching' ||
      context.intent !== 'book' ||
      (!context.eligibility.eligible &&
        context.eligibility.reasons.includes('active_series_exists')),
    'active Series booking context must be ineligible',
  );
  validateLegacyAvailability(context);
};

export const validateOperationStatus = (
  operation: AppointmentOperationStatus,
): void => {
  if (operation.status === 'confirmed') {
    failUnless(
      operation.appointmentIds.length > 0,
      'confirmed operation requires nonempty Appointment IDs',
    );
    failUnless(
      operation.rejection === null,
      'confirmed operation requires a null rejection',
    );
    failUnless(
      operation.retryAfterSeconds === null,
      'confirmed operation requires a null retry',
    );
    return;
  }

  if (operation.status === 'pending') {
    failUnless(
      operation.appointmentIds.length > 0,
      'pending operation requires nonempty Appointment IDs',
    );
    failUnless(
      operation.rejection === null,
      'pending operation requires a null rejection',
    );
    return;
  }

  failUnless(
    operation.appointmentIds.length === 0,
    'rejected operation requires empty Appointment IDs',
  );
  failUnless(
    operation.rejection !== null,
    'rejected operation requires a non-null rejection',
  );
  failUnless(
    operation.retryAfterSeconds === null,
    'rejected operation requires a null retry',
  );
};
